import React, { useMemo } from 'react';

import { Platform } from 'react-native';

import { useSelector } from 'react-redux';

import { RouteProp } from '@react-navigation/core/lib/typescript/src/types';
import {
    HeaderStyleInterpolators,
    StackHeaderProps,
    StackNavigationProp,
    TransitionPresets,
    createStackNavigator,
} from '@react-navigation/stack';
import {
    StackHeaderMode,
    StackHeaderOptions,
} from '@react-navigation/stack/src/types';

import { SCREEN } from 'router/screens';
import { ModularScreens, RouteNavigationParams } from 'router/types';

import { MenuItemType } from 'store/railsBuilder/reducer.types';
import { selectMainMenuItemsById } from 'store/railsBuilder/selectors';

import { COLOR } from 'utils/colors';
import useHeaderHeight from 'utils/hooks/useHeaderHeight';
import { isWeb } from 'utils/responsiveness';

import CollectionPage from 'screens/Collection';
import DetailPage from 'screens/Detail';
import DetailVodHighUsageRestriction from 'screens/DetailVodHighUsageRestriction';
import Epg from 'screens/Epg';
import ModularPage from 'screens/Modular';
import Search from 'screens/Search';
import SettingsPage from 'screens/Settings';
import AboutPage from 'screens/Settings/About';
import AudioSubtitles from 'screens/Settings/AudioSubtitles';
import Audio from 'screens/Settings/AudioSubtitles/Audio';
import Subtitles from 'screens/Settings/AudioSubtitles/Subtitles';
import SubtitlesFontSize from 'screens/Settings/AudioSubtitles/SubtitlesFontSize';
import BackendInfoPage from 'screens/Settings/BackendInfo';
import ChannelLineup from 'screens/Settings/ChannelLineup';
import EditMyList from 'screens/Settings/ChannelLineup/EditMyList/EditMyList';
import MyList from 'screens/Settings/ChannelLineup/MyList/MyList';
import StandardList from 'screens/Settings/ChannelLineup/StandardList/StandardList';
import SwitchChannelList from 'screens/Settings/ChannelLineup/SwitchChannelList/SwitchChannelList';
import EpgDisplay from 'screens/Settings/EpgDisplay';
import EpgDisplayOptions from 'screens/Settings/EpgDisplay/EpgDisplayOptions';
import HelpPage from 'screens/Settings/Help';
import InternetConnection from 'screens/Settings/InternetConnection';
import LanguagePage from 'screens/Settings/Language';
import Logout from 'screens/Settings/Logout';
import ParentalControl from 'screens/Settings/ParentalControl';
import ParentalControlAndPin from 'screens/Settings/ParentalControlAndPin';
import Quota from 'screens/Settings/Quota';
import Subscriptions from 'screens/Settings/Subscriptions';
import TermsConditions from 'screens/Settings/TermsConditions';
import {
    SCREEN_PARENTAL_CONTORL_TITLE,
    SETTINGS_APP_LANGUAGE,
    SETTINGS_AUDIO_LANGUAGE,
    SETTINGS_AUDIO_SUBTITLE_FONT_SIZE,
    SETTINGS_BACKEND_INFORMATION,
    SETTINGS_CHANNEL_LINEUP,
    SETTINGS_CHANNEL_LINEUP_MY_LIST,
    SETTINGS_CHANNEL_LINEUP_STANDARD_LIST,
    SETTINGS_DISPLAY,
    SETTINGS_LANGUAGE_AUDIO_SUBTITLES,
    SETTINGS_PARENTAL_CONTROL,
    SETTINGS_RN_INTERNET_CONNECTION_OPTIONS,
    SETTINGS_SECTION_TITLE,
    SETTINGS_SUBTITLE_LANGUAGE,
    SETTINGS_SYSTEM_INFORMATION,
} from 'screens/Settings/languagesConst';

import RootNavigationHeader from 'components/NavigationHeader/RootNavigationHeader';
import SimpleNavigationHeaderLeft from 'components/NavigationHeader/SimpleNavigationHeader/Left';
import SimpleNavigationHeaderRight from 'components/NavigationHeader/SimpleNavigationHeader/Right';
import SimpleNavigationHeaderTitle from 'components/NavigationHeader/SimpleNavigationHeader/Title';

const Stack = createStackNavigator();

function RootScreenNavigator({
    navigation,
    route,
}: {
    navigation: StackNavigationProp<
        RouteNavigationParams,
        ModularScreens | SCREEN.TV_GUIDE
    >;
    route: RouteProp<RouteNavigationParams, ModularScreens | SCREEN.TV_GUIDE>;
}) {
    const mainMenuItemsById = useSelector(selectMainMenuItemsById);

    const isEpgScreen =
        !!route.params?.id &&
        mainMenuItemsById[route.params.id]?.type === MenuItemType.EPG;
    const headerHeight = useHeaderHeight();

    const headerStyle = useMemo(
        () => ({
            height: headerHeight,
            shadowColor: 'transparent',
        }),
        [headerHeight],
    );

    const stackNavigatorOptions = useMemo(
        () => ({
            headerShown: true,
            headerStyle: {
                shadowColor: 'transparent',
                borderBottomWidth: 1,
                borderBottomColor: 'rgba(255, 255, 255, 0.1)',
                backgroundColor: COLOR.BLACK,
            },
            headerTitle: () =>
                Platform.select({
                    web: null,
                    default: <SimpleNavigationHeaderTitle />,
                }),
            headerRight: () =>
                Platform.select({
                    web: null,
                    default: <SimpleNavigationHeaderRight />,
                }),
            headerLeft: () =>
                Platform.select({
                    web: null,
                    default: <SimpleNavigationHeaderLeft />,
                }),
            headerTitleAlign: 'left' as StackHeaderOptions['headerTitleAlign'],
            cardStyle: {
                backgroundColor: 'transparent',
            },
            headerMode: 'screen' as StackHeaderMode,
            ...(isWeb && {
                headerStyleInterpolator: HeaderStyleInterpolators.forFade,
                header: ({ progress }: StackHeaderProps) => (
                    <RootNavigationHeader
                        navigation={navigation}
                        style={headerStyle}
                        progress={progress}
                    />
                ),
            }),
        }),
        [navigation, headerStyle],
    );

    return (
        <Stack.Navigator
            screenOptions={stackNavigatorOptions}
            initialRouteName={isEpgScreen ? SCREEN.EPG : SCREEN.MODULAR_ROOT}
        >
            {isEpgScreen ? (
                <Stack.Screen
                    name={SCREEN.EPG}
                    // @ts-ignore
                    component={Epg}
                />
            ) : (
                <Stack.Screen
                    name={SCREEN.MODULAR_ROOT}
                    // @ts-ignore
                    component={ModularPage}
                    options={{ animationEnabled: false }}
                    initialParams={{ id: route.params?.id }}
                />
            )}
            {/* @ts-ignore */}
            <Stack.Screen name={SCREEN.COLLECTION} component={CollectionPage} />

            {/* @ts-ignore */}
            <Stack.Screen name={SCREEN.DETAILS} component={DetailPage} />

            {isWeb && (
                <Stack.Screen
                    name={SCREEN.DETAILS_VOD_HIGH_USAGE_RESTRICTION}
                    component={DetailVodHighUsageRestriction}
                />
            )}

            <Stack.Screen
                name={SCREEN.SEARCH}
                // @ts-ignore
                component={Search}
                options={Platform.select({
                    web: {
                        presentation: 'modal',
                        headerShown: false,
                        animationEnabled: false,
                    },
                    default: {
                        presentation: 'modal',
                        headerShown: false,
                        ...TransitionPresets.ModalSlideFromBottomIOS,
                    },
                })}
            />

            {/* =============== */}
            {/* Settings routes */}
            {/* =============== */}
            <Stack.Screen
                name={SCREEN.SETTINGS}
                component={SettingsPage}
                initialParams={Platform.select({
                    web: undefined,
                    default: {
                        title: SETTINGS_SECTION_TITLE,
                        shouldSetAccessibilityFocus: true,
                    },
                })}
            />
            <Stack.Screen
                name={SCREEN.LANGUAGE}
                component={LanguagePage}
                initialParams={Platform.select({
                    web: undefined,
                    default: {
                        title: SETTINGS_APP_LANGUAGE,
                        shouldSetAccessibilityFocus: true,
                    },
                })}
            />
            <Stack.Screen
                name={SCREEN.PARENTAL_CONTROL_AND_PIN}
                component={ParentalControlAndPin}
                initialParams={Platform.select({
                    web: undefined,
                    default: {
                        title: SCREEN_PARENTAL_CONTORL_TITLE,
                        shouldSetAccessibilityFocus: true,
                    },
                })}
            />
            <Stack.Screen
                name={SCREEN.PARENTAL_CONTROL}
                component={ParentalControl}
                initialParams={Platform.select({
                    web: undefined,
                    default: {
                        title: SETTINGS_PARENTAL_CONTROL,
                        shouldSetAccessibilityFocus: true,
                    },
                })}
            />
            <Stack.Screen
                name={SCREEN.AUDIO_AND_SUBTITLES}
                component={AudioSubtitles}
                initialParams={Platform.select({
                    web: undefined,
                    default: {
                        title: SETTINGS_LANGUAGE_AUDIO_SUBTITLES,
                        shouldSetAccessibilityFocus: true,
                    },
                })}
            />
            <Stack.Screen
                name={SCREEN.SETTINGS_AUDIO}
                component={Audio}
                initialParams={Platform.select({
                    web: undefined,
                    default: {
                        title: SETTINGS_AUDIO_LANGUAGE,
                        shouldSetAccessibilityFocus: true,
                    },
                })}
            />
            <Stack.Screen
                name={SCREEN.SETTINGS_SUBTITLES}
                component={Subtitles}
                initialParams={Platform.select({
                    web: undefined,
                    default: {
                        title: SETTINGS_SUBTITLE_LANGUAGE,
                        shouldSetAccessibilityFocus: true,
                    },
                })}
            />
            <Stack.Screen
                name={SCREEN.SETTINGS_SUBTITLES_FONT_SIZE}
                component={SubtitlesFontSize}
                initialParams={Platform.select({
                    web: undefined,
                    default: {
                        title: SETTINGS_AUDIO_SUBTITLE_FONT_SIZE,
                        shouldSetAccessibilityFocus: true,
                    },
                })}
            />
            <Stack.Screen
                name={SCREEN.CHANNELS_LINEUP}
                component={ChannelLineup}
                initialParams={Platform.select({
                    web: undefined,
                    default: {
                        title: SETTINGS_CHANNEL_LINEUP,
                        shouldSetAccessibilityFocus: true,
                    },
                })}
            />
            <Stack.Screen
                name={SCREEN.STANDARD_LIST}
                component={StandardList}
                initialParams={Platform.select({
                    web: undefined,
                    default: {
                        title: SETTINGS_CHANNEL_LINEUP_STANDARD_LIST,
                        shouldSetAccessibilityFocus: true,
                    },
                })}
            />
            <Stack.Screen
                name={SCREEN.MY_LIST}
                component={MyList}
                initialParams={Platform.select({
                    web: undefined,
                    default: {
                        title: SETTINGS_CHANNEL_LINEUP_MY_LIST,
                        shouldSetAccessibilityFocus: true,
                    },
                })}
            />
            <Stack.Screen
                name={SCREEN.EDIT_MY_LIST}
                component={EditMyList}
                initialParams={Platform.select({
                    web: undefined,
                    default: {
                        title: SETTINGS_CHANNEL_LINEUP_MY_LIST,
                        shouldSetAccessibilityFocus: true,
                    },
                })}
            />
            <Stack.Screen
                name={SCREEN.SWITCH_CHANNEL_LIST}
                component={SwitchChannelList}
                initialParams={Platform.select({
                    web: undefined,
                    default: {
                        title: SETTINGS_CHANNEL_LINEUP,
                        shouldSetAccessibilityFocus: true,
                    },
                })}
            />
            <Stack.Screen
                name={SCREEN.ABOUT}
                component={AboutPage}
                initialParams={Platform.select({
                    web: undefined,
                    default: {
                        title: SETTINGS_SYSTEM_INFORMATION,
                        shouldSetAccessibilityFocus: true,
                    },
                })}
            />
            <Stack.Screen
                name={SCREEN.SETTINGS_HELP}
                component={HelpPage}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name={SCREEN.SETTINGS_RECORDING_QUOTA}
                component={Quota}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name={SCREEN.SETTINGS_INTERNET_CONNECTION}
                component={InternetConnection}
                initialParams={Platform.select({
                    web: undefined,
                    default: { title: SETTINGS_RN_INTERNET_CONNECTION_OPTIONS },
                })}
            />
            <Stack.Screen
                name={SCREEN.SETTINGS_SUBSCRIPTIONS}
                component={Subscriptions}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name={SCREEN.SETTINGS_LOGOUT}
                component={Logout}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name={SCREEN.SETTINGS_BACKEND_INFO}
                component={BackendInfoPage}
                initialParams={Platform.select({
                    web: undefined,
                    default: {
                        title: SETTINGS_BACKEND_INFORMATION,
                        shouldSetAccessibilityFocus: true,
                    },
                })}
            />
            <Stack.Screen
                name={SCREEN.SETTINGS_TERMS_CONDITIONS}
                component={TermsConditions}
                options={{ headerShown: false }}
            />
            <Stack.Screen
                name={SCREEN.SETTINGS_EPG_DISPLAY}
                component={EpgDisplay}
                initialParams={Platform.select({
                    web: undefined,
                    default: { title: SETTINGS_DISPLAY },
                })}
            />
            <Stack.Screen
                name={SCREEN.SETTINGS_EPG_DISPLAY_OPTIONS}
                component={EpgDisplayOptions}
                initialParams={Platform.select({
                    web: undefined,
                    default: { title: SETTINGS_DISPLAY },
                })}
            />
        </Stack.Navigator>
    );
}

export default RootScreenNavigator;
